/*----------------------------------
- DOMAINING NEWS ROUTES
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { NotFound } from '@common/errors';
import type ApiClient from '@common/router/request/api';
import type { TFetcher } from '@common/router/request/api';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import {
    FACETED_SEARCH_ROBOTS_DIRECTIVES,
    PUBLIC_INDEX_ROBOTS_DIRECTIVES,
} from '@/common/catalogs/crawlerPolicy';
import {
    DOMAINING_NEWS_ARCHIVE_GATEWAY_PATH,
    DOMAINING_NEWS_EDITION_GATEWAY_PATH,
    DOMAINING_NEWS_LEAD_EDITION_GATEWAY_PATH,
} from '@/common/contracts/domainingNewsGateway';
import { WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY } from '@/common/catalogs/landing/openGraphBanners';
import {
    buildDomainingNewsArchiveStructuredData,
    buildDomainingNewsEditionStructuredData,
    type WebsiteJsonLdNode,
} from '@/client/pages/landing/seo/structuredData';
import {
    DOMAINING_NEWS_FEED_PATH,
    DOMAINING_NEWS_INDEX_PATH,
    DOMAINING_NEWS_MARKDOWN_PATH,
    DOMAINING_NEWS_SHARE_IMAGE_ALT,
    DOMAINING_NEWS_SHARE_IMAGE_CONTENT_TYPE,
    DOMAINING_NEWS_SHARE_IMAGE_HEIGHT,
    DOMAINING_NEWS_SHARE_IMAGE_WIDTH,
    domainingNewsEditionMarkdownPath,
    domainingNewsEditionPath,
    domainingNewsPagePath,
    domainingNewsShareImagePath,
    type PublishedNewsEdition,
    type PublishedNewsListResponse,
} from '@uniquedomains/components/contracts/news/public';
import DomainingNewsArchivePage from '../page/DomainingNewsArchivePage';
import DomainingNewsArticlePage from '../page/DomainingNewsArticlePage';
import { getDomainingNewsCategoryLabels } from '@uniquedomains/components/news/presentation';
import {
    buildDomainingNewsArchiveDescription,
    buildDomainingNewsArchivePath,
    buildDomainingNewsArchiveTitle,
    DOMAINING_NEWS_PAGE_SIZE,
    resolveDomainingNewsArchivePage,
    resolveDomainingNewsCategoryCode,
    resolveDomainingNewsDateKey,
} from './presentation';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import type { PublicReadResult } from '@/common/contracts/publicReadResult';
import {
    PUBLIC_REVALIDATE_HEADERS,
    PublicPageUnavailable,
    setPublicUnavailableHeaders,
} from '@/client/pages/landing/lib/publicUnavailable';

/*----------------------------------
- TYPES
----------------------------------*/

type NewsProductController = {
    Navigation: {
        GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation>;
    };
};

type NewsRouteDataContext = {
    Product: NewsProductController;
    api: Pick<ApiClient, 'createFetcher'>;
    request: NewsRouteRequest;
};

type NewsRouteRequest = {
    url: string;
    res?: {
        setHeader: (name: string, value: string) => void;
    };
};

type NewsPageDocument = {
    description?: string;
    head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
    jsonld: WebsiteJsonLdNode[];
    metas: Record<string, string | Date | null | undefined>;
    title?: string;
    url: string;
};

type NewsRouteResponse = {
    canonicalUrl?: URL;
    redirect?: (url: string, code?: number) => void;
    setHeaders?: (headers: Record<string, string>) => void;
};

type ArchiveRenderContext = {
    archiveResult: PublicReadResult<PublishedNewsListResponse>;
    /** Only requested on the unfiltered first page; null everywhere else. */
    leadEditionResult?: PublicReadResult<PublishedNewsEdition | null> | null;
    navigation: LandingHeaderDomainsNavigation;
    page: NewsPageDocument;
    request: NewsRouteRequest;
    response?: NewsRouteResponse;
    Router: { url: (path: string) => string };
};

type EditionRenderContext = {
    dateKey: string;
    editionResult: PublicReadResult<PublishedNewsEdition | null>;
    navigation: LandingHeaderDomainsNavigation;
    page: NewsPageDocument;
    request: NewsRouteRequest;
    response?: NewsRouteResponse;
    Router: { url: (path: string) => string };
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const NEWS_UNAVAILABLE_MESSAGE = 'Domaining News is temporarily unavailable. Please try again shortly.';

/**
 * Archive fallback banner. Editions point at their own rendered card instead, and
 * the share-card route falls back to this same file when a card cannot be built.
 */
const NEWS_SOCIAL_IMAGE_PATH = `${WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY}/og-domaining-news.png`;

/*----------------------------------
- HELPERS
----------------------------------*/

const addNewsDiscoveryLinks = ({
    markdownUrl,
    page,
    robotsDirectives = PUBLIC_INDEX_ROBOTS_DIRECTIVES,
    rssUrl,
}: {
    markdownUrl: string;
    page: NewsPageDocument;
    robotsDirectives?: string;
    rssUrl: string;
}) => {
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'application/rss+xml',
        title: 'Unique Domains Domaining News RSS',
        href: rssUrl,
    });
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: 'Markdown version',
        href: markdownUrl,
    });
    page.head.push({
        $: 'meta',
        name: 'robots',
        content: robotsDirectives,
    });
};

/*----------------------------------
- ARCHIVE
----------------------------------*/

export const buildDomainingNewsArchiveRouteData = ({
    Product,
    api,
    request,
}: NewsRouteDataContext) => {
    const requestedPage = resolveDomainingNewsArchivePage(request.url);
    if (requestedPage === null) throw new NotFound();
    const categoryCode = resolveDomainingNewsCategoryCode(request.url);
    // The lead card only exists on the unfiltered first page, so no other view
    // pays for the second read.
    const wantsLeadEdition = requestedPage === 1 && categoryCode === null;

    return {
        archiveResult: api.createFetcher<PublicReadResult<PublishedNewsListResponse>>(
            'POST',
            DOMAINING_NEWS_ARCHIVE_GATEWAY_PATH,
            {
                page: requestedPage,
                pageSize: DOMAINING_NEWS_PAGE_SIZE,
                ...(categoryCode ? { categoryCode } : {}),
            },
        ),
        leadEditionResult: wantsLeadEdition
            ? api.createFetcher<PublicReadResult<PublishedNewsEdition | null>>(
                  'POST',
                  DOMAINING_NEWS_LEAD_EDITION_GATEWAY_PATH,
                  {},
              )
            : null,
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    };
};

export const renderDomainingNewsArchiveRoute = ({
    archiveResult,
    leadEditionResult,
    navigation,
    page,
    request,
    response,
    Router,
}: ArchiveRenderContext) => {
    const requestedPage = resolveDomainingNewsArchivePage(request.url);
    if (requestedPage === null) throw new NotFound();
    const categoryCode = resolveDomainingNewsCategoryCode(request.url);
    const requestedPath = new URL(request.url, 'https://unique.domains').pathname.replace(/\/+$/, '');

    if (archiveResult.status === 'unavailable') {
        setPublicUnavailableHeaders(request, response);
        throw new PublicPageUnavailable(NEWS_UNAVAILABLE_MESSAGE);
    }
    if (requestedPath.endsWith('/page/1')) {
        response?.redirect?.(Router.url(DOMAINING_NEWS_INDEX_PATH), 301);
        return null;
    }
    const archive = archiveResult.value;
    if (
        archive.page !== requestedPage ||
        requestedPage < 1 ||
        (requestedPage > 1 && archive.totalPages === 0) ||
        (archive.totalPages > 0 && requestedPage > archive.totalPages)
    ) {
        throw new NotFound();
    }

    // A filtered view is a facet of the same archive page, so it canonicalizes to
    // the unfiltered URL and is served `noindex, follow`: crawlers keep walking
    // the edition links without ten near-duplicate archives entering the index.
    const canonicalPath =
        requestedPage === 1 ? DOMAINING_NEWS_INDEX_PATH : domainingNewsPagePath(requestedPage);
    const canonicalUrl = Router.url(canonicalPath);
    const imageUrl = Router.url(NEWS_SOCIAL_IMAGE_PATH);
    const title = buildDomainingNewsArchiveTitle(requestedPage);
    const description = buildDomainingNewsArchiveDescription(requestedPage);

    page.title = title;
    page.description = description;
    page.url = canonicalUrl;
    page.metas = {
        ...page.metas,
        'og:description': description,
        'og:image': imageUrl,
        'og:image:alt': DOMAINING_NEWS_SHARE_IMAGE_ALT,
        'og:image:height': String(DOMAINING_NEWS_SHARE_IMAGE_HEIGHT),
        'og:image:type': DOMAINING_NEWS_SHARE_IMAGE_CONTENT_TYPE,
        'og:image:width': String(DOMAINING_NEWS_SHARE_IMAGE_WIDTH),
        'og:title': title,
        'og:type': 'website',
        'og:url': canonicalUrl,
        'twitter:card': 'summary_large_image',
        'twitter:description': description,
        'twitter:image': imageUrl,
        'twitter:image:alt': DOMAINING_NEWS_SHARE_IMAGE_ALT,
        'twitter:title': title,
        'twitter:url': canonicalUrl,
    };
    addNewsDiscoveryLinks({
        markdownUrl: Router.url(DOMAINING_NEWS_MARKDOWN_PATH),
        page,
        robotsDirectives: categoryCode
            ? FACETED_SEARCH_ROBOTS_DIRECTIVES
            : PUBLIC_INDEX_ROBOTS_DIRECTIVES,
        rssUrl: Router.url(DOMAINING_NEWS_FEED_PATH),
    });
    if (requestedPage > 1) {
        page.head.push({
            $: 'link',
            rel: 'prev',
            href: Router.url(buildDomainingNewsArchivePath(requestedPage - 1, categoryCode)),
        });
    }
    if (requestedPage < archive.totalPages) {
        page.head.push({
            $: 'link',
            rel: 'next',
            href: Router.url(buildDomainingNewsArchivePath(requestedPage + 1, categoryCode)),
        });
    }
    page.jsonld.push(
        ...buildDomainingNewsArchiveStructuredData({
            canonicalUrl,
            description,
            editions: archive.items,
            title,
        }),
    );

    if (response) response.canonicalUrl = new URL(canonicalUrl);
    response?.setHeaders?.(PUBLIC_REVALIDATE_HEADERS);

    // A lead-card failure degrades to the plain first row instead of taking the
    // archive down, and a stale card can never outlive its own row.
    const leadEdition =
        leadEditionResult?.status === 'success'
        && leadEditionResult.value
        && leadEditionResult.value.dateKey === archive.items[0]?.dateKey
            ? leadEditionResult.value
            : null;

    return (
        <DomainingNewsArchivePage
            categoryCode={categoryCode}
            editions={archive.items}
            leadEdition={leadEdition}
            navigation={navigation}
            pageNumber={requestedPage}
            totalItems={archive.totalItems}
            totalPages={archive.totalPages}
        />
    );
};

/*----------------------------------
- EDITION
----------------------------------*/

export const buildDomainingNewsEditionRouteData = ({
    Product,
    api,
    request,
}: NewsRouteDataContext) => {
    const dateKey = resolveDomainingNewsDateKey(request.url);

    return {
        dateKey,
        editionResult: api.createFetcher<PublicReadResult<PublishedNewsEdition | null>>(
            'POST',
            DOMAINING_NEWS_EDITION_GATEWAY_PATH,
            { dateKey },
        ),
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    };
};

export const renderDomainingNewsEditionRoute = ({
    dateKey,
    editionResult,
    navigation,
    page,
    request,
    response,
    Router,
}: EditionRenderContext) => {
    if (editionResult.status === 'unavailable') {
        setPublicUnavailableHeaders(request, response);
        throw new PublicPageUnavailable(NEWS_UNAVAILABLE_MESSAGE);
    }
    const edition = editionResult.value;
    if (!edition || edition.dateKey !== dateKey) throw new NotFound();

    const canonicalUrl = Router.url(domainingNewsEditionPath(edition.dateKey));
    const markdownUrl = Router.url(domainingNewsEditionMarkdownPath(edition.dateKey));
    // Per-edition card rendered by Product and served from the marketing origin.
    // It falls back to the shared banner when the card cannot be built.
    const imageUrl = Router.url(domainingNewsShareImagePath(edition.dateKey));
    const categoryLabels = getDomainingNewsCategoryLabels(edition.categoryCodes);

    page.title = edition.headline;
    page.description = edition.seoDescription;
    page.url = canonicalUrl;
    page.metas = {
        ...page.metas,
        'og:description': edition.seoDescription,
        'og:image': imageUrl,
        'og:image:alt': DOMAINING_NEWS_SHARE_IMAGE_ALT,
        'og:image:height': String(DOMAINING_NEWS_SHARE_IMAGE_HEIGHT),
        'og:image:type': 'image/png',
        'og:image:width': String(DOMAINING_NEWS_SHARE_IMAGE_WIDTH),
        'og:title': edition.headline,
        'og:type': 'article',
        'og:url': canonicalUrl,
        'twitter:card': 'summary_large_image',
        'twitter:description': edition.seoDescription,
        'twitter:image': imageUrl,
        'twitter:image:alt': DOMAINING_NEWS_SHARE_IMAGE_ALT,
        'twitter:title': edition.headline,
        'twitter:url': canonicalUrl,
    };
    addNewsDiscoveryLinks({
        markdownUrl,
        page,
        rssUrl: Router.url(DOMAINING_NEWS_FEED_PATH),
    });
    page.head.push({
        $: 'meta',
        property: 'article:published_time',
        content: edition.publishedAt,
    });
    page.head.push({
        $: 'meta',
        property: 'article:modified_time',
        content: edition.updatedAt,
    });
    page.head.push({
        $: 'meta',
        property: 'article:author',
        content: 'Unique Domains',
    });
    categoryLabels.forEach((categoryLabel) => {
        page.head.push({
            $: 'meta',
            property: 'article:section',
            content: categoryLabel,
        });
        page.head.push({
            $: 'meta',
            property: 'article:tag',
            content: categoryLabel,
        });
    });
    page.jsonld.push(
        ...buildDomainingNewsEditionStructuredData({
            canonicalUrl,
            categoryLabels,
            edition,
            imageUrl,
        }),
    );

    if (response) response.canonicalUrl = new URL(canonicalUrl);
    response?.setHeaders?.(PUBLIC_REVALIDATE_HEADERS);

    return (
        <DomainingNewsArticlePage
            canonicalUrl={canonicalUrl}
            edition={edition}
            navigation={navigation}
        />
    );
};
